import { useEffect, useRef, useState } from "react";
import { isStaleWrite, staleWriteCurrent } from "@repo/api-client";
import type { StaffBookingResponse, StaffBookingSummaryResponse } from "@repo/api-client";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useVenue } from "../shell/VenueContext";
import { BookingConflictModal } from "./BookingConflictModal";
import { Overlay } from "./ManualEntryDialog";
import { mintCancelStepUp, useCancelBooking } from "./mutations";
import { bookingRejectionOf, rejectionMessageKey } from "./rejections";
import { banner, dangerButton, fieldLabel, secondaryButton, textInput } from "./styles";

/**
 * Cancel flow (BK-8). A waiter cancelling a live booking must first present a manager PIN, which
 * mints a single-use `CANCEL_CONFIRMED_BOOKING` step-up (ST-4) that rides on the cancel as a header;
 * a manager cancels directly. A 409 opens the conflict modal; a SEATED/terminal booking is rejected
 * server-side (the affordance is already hidden, this is defence in depth).
 */
export function CancelDialog({
  booking,
  needsStepUp,
  onClose,
  onCancelled,
}: {
  booking: StaffBookingSummaryResponse;
  needsStepUp: boolean;
  onClose: () => void;
  onCancelled: (b: StaffBookingResponse) => void;
}) {
  const { venueId } = useVenue();
  const { t } = useTranslation();
  const cancel = useCancelBooking(venueId, booking.bookingId ?? "");
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => headingRef.current?.focus(), []);

  const [reason, setReason] = useState("");
  const [pin, setPin] = useState("");
  const [authId, setAuthId] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<StaffBookingResponse | null>(null);
  const [version, setVersion] = useState(booking.version ?? 0);

  async function doCancel(v: number) {
    setError(null);
    setBusy(true);
    let stepUpAuthId = authId;
    try {
      if (needsStepUp && !stepUpAuthId) {
        const res = await mintCancelStepUp(venueId, pin, booking.bookingId ?? "");
        stepUpAuthId = res.authorizationId;
        setAuthId(stepUpAuthId);
      }
    } catch {
      setBusy(false);
      setError(t("bookings.cancel.stepUpFailed"));
      return;
    }
    cancel.mutate(
      { body: { version: v, reason: reason || null }, stepUpAuthId },
      {
        onSuccess: (b) => onCancelled(b),
        onSettled: () => setBusy(false),
        onError: (err) => {
          if (isStaleWrite(err)) {
            const current = staleWriteCurrent<StaffBookingResponse>(err);
            if (current) setConflict(current);
            else setError(t("bookings.error.cancelFailed"));
            return;
          }
          const rej = bookingRejectionOf(err);
          setError(rej ? t(rejectionMessageKey(rej)) : t("bookings.error.cancelFailed"));
        },
      },
    );
  }

  return (
    <>
      <Overlay onClose={onClose} label={t("bookings.cancel.title")}>
        <h2
          ref={headingRef}
          tabIndex={-1}
          className={css({ fontSize: "lg", fontWeight: "bold", outline: "none" })}
        >
          {t("bookings.cancel.title")}
        </h2>
        <p className={css({ fontSize: "sm", color: "fg.muted" })}>
          {t("bookings.cancel.confirm", { ref: booking.externalBookingId ?? "" })}
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void doCancel(version);
          }}
          className={css({ display: "flex", flexDirection: "column", gap: "3" })}
        >
          {error ? (
            <p role="alert" className={banner("error")}>
              {error}
            </p>
          ) : null}

          <label className={fieldLabel}>
            {t("bookings.cancel.reason")}
            <input
              className={textInput}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>

          {needsStepUp ? (
            <div className={banner("warn")}>
              <span className={css({ fontWeight: "medium" })}>
                {t("bookings.cancel.stepUpTitle")}
              </span>
              <span>{t("bookings.cancel.stepUpHint")}</span>
              <label className={fieldLabel}>
                {t("bookings.cancel.managerPin")}
                <input
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  className={textInput}
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                />
              </label>
            </div>
          ) : null}

          <div className={actions}>
            <button type="button" className={secondaryButton} onClick={onClose}>
              {t("bookings.action.back")}
            </button>
            <button
              type="submit"
              className={dangerButton}
              disabled={busy || (needsStepUp && pin.trim() === "")}
            >
              {busy ? t("bookings.action.saving") : t("bookings.cancel.submit")}
            </button>
          </div>
        </form>
      </Overlay>

      {conflict ? (
        <BookingConflictModal
          current={conflict}
          onReload={() => {
            setVersion(conflict.version ?? version);
            setConflict(null);
          }}
          onReconfirm={() => {
            const v = conflict.version ?? version;
            setVersion(v);
            setConflict(null);
            void doCancel(v);
          }}
        />
      ) : null}
    </>
  );
}

const actions = css({ display: "flex", gap: "2", justifyContent: "flex-end", flexWrap: "wrap" });
